/**
 * Mapbox GL is loaded from the CDN at runtime rather than bundled — an npm
 * dependency would land in the shared `vendors` chunk and tax every page. We
 * type the global loosely instead of taking a build-time dep.
 *
 * Shared so every map component loads the same version through the same
 * process-wide promise, rather than each carrying its own copy of this.
 */
export type MapboxGL = any;

export const MAPBOX_VERSION = '3.7.0';
export const MAPBOX_JS = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
export const MAPBOX_CSS = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;

/** Process-wide loader so the CDN script/stylesheet are injected at most once */
let mapboxPromise: Promise<MapboxGL> | null = null;

export function loadMapboxGl(): Promise<MapboxGL> {
  const w = window as any;
  if (w.mapboxgl) return Promise.resolve(w.mapboxgl);
  if (mapboxPromise) return mapboxPromise;

  mapboxPromise = new Promise<MapboxGL>((resolve, reject) => {
    if (!document.querySelector('link[data-mapbox-gl]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = MAPBOX_CSS;
      css.setAttribute('data-mapbox-gl', '');
      document.head.appendChild(css);
    }

    const script = document.createElement('script');
    script.src = MAPBOX_JS;
    script.async = true;
    script.setAttribute('data-mapbox-gl', '');
    script.onload = () =>
      w.mapboxgl ? resolve(w.mapboxgl) : reject(new Error('mapbox-gl global missing after load'));
    script.onerror = () => {
      mapboxPromise = null; // allow a later retry
      reject(new Error('Failed to load mapbox-gl from CDN'));
    };
    document.head.appendChild(script);
  });

  return mapboxPromise;
}
